import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Autocomplete,
    Alert,
    AlertTitle,
    CircularProgress,
    Typography,
    Switch,
    FormControlLabel,
    Tooltip
} from '@mui/material';
import { Close } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useHasPermission } from "../utils/permissionUtils";
import RetainerOverageNotice from "./RetainerOverageNotice";
import { useRetainerStatus } from "../hooks/useRetainerStatus";
import AppDatePicker from "./AppDatePicker";
import { formatDayLong, toLocalDay } from "../utils/dateUtils";
import { getClients, getClientAccounts } from "../redux/ducks/client";
import { getAccountProjects } from "../redux/ducks/account";
import { getInvoicedPeriodForDate } from "../redux/ducks/invoices";

const TopAnchoredDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-container": {
        alignItems: "flex-start",
    },
    "& .MuiDialog-paper": {
        marginTop: theme.spacing(6),
    },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: "absolute",
    right: 8,
    top: 8,
}));

const FormContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
}));

const TimeDateRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    "& > *": {
        flex: 1,
    },
}));

const HelperLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const KeepOpenToggle = styled(FormControlLabel)(({ theme }) => ({
    marginRight: 'auto',
    marginLeft: theme.spacing(0.5),
    '& .MuiFormControlLabel-label': {
        fontSize: theme.typography.size.md,
        color: theme.palette.text.secondary,
    },
}));

const AddTimeRecordDialog = ({ open, onClose, onAddTime, accountId, defaultDate, pickTarget = false }) => {
    const dispatch = useDispatch();
    const [summary, setSummary] = useState('');
    const [time, setTime] = useState('');
    const [date, setDate] = useState(toLocalDay());
    const [selectedUser, setSelectedUser] = useState('');
    const hasPermission = useHasPermission();
    const canAddOthersTime = hasPermission("addOthersTime");
    const canSeeInternal = hasPermission("canSeeInternal");
    const accountUsers = useSelector(state => state.accountUsers.users);

    const [clientOptions, setClientOptions] = useState([]);
    const [accountOptions, setAccountOptions] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const clientsRequestedRef = useRef(false);
    const accountsRequestRef = useRef(0);
    const projectsRequestRef = useRef(0);
    const [client, setClient] = useState(null);
    const [account, setAccount] = useState(null);
    const [project, setProject] = useState(null);

    const [invoicedPeriod, setInvoicedPeriod] = useState(null);
    const invoicedRequestRef = useRef(0);
    const submitLockRef = useRef(false);

    const effectiveAccountId = pickTarget ? account?._id : accountId;
    const { status: retainerStatus } = useRetainerStatus({ accountId: effectiveAccountId, enabled: open });
    const [overageOverride, setOverageOverride] = useState(false);
    const [keepOpen, setKeepOpen] = useState(false);
    const isOverageBlocked = retainerStatus?.level === "block" && !overageOverride;

    useEffect(() => {
        if (open) {
            submitLockRef.current = false;
            setOverageOverride(false);
            setKeepOpen(false);
            setSummary('');
            setTime('');
            setDate(defaultDate || toLocalDay());
            setSelectedUser('');
            setClient(null);
            setAccount(null);
            setProject(null);
            setAccountOptions([]);
            setProjectOptions([]);
            setInvoicedPeriod(null);
        }
    }, [open, defaultDate]);

    useEffect(() => {
        if (!open || !pickTarget || clientsRequestedRef.current) return;
        clientsRequestedRef.current = true;
        setClientsLoading(true);
        dispatch(getClients(0, 0, (data) => {
            setClientsLoading(false);
            if (!data) {
                clientsRequestedRef.current = false;
                return;
            }
            setClientOptions(Array.isArray(data) ? data : data.clients || []);
        }));
    }, [open, pickTarget, dispatch]);

    useEffect(() => {
        if (!pickTarget || !client?._id) {
            accountsRequestRef.current += 1;
            setAccountOptions([]);
            setAccountsLoading(false);
            return;
        }
        const requestId = ++accountsRequestRef.current;
        setAccountsLoading(true);
        dispatch(getClientAccounts(client._id, (data) => {
            if (accountsRequestRef.current !== requestId) return;
            setAccountsLoading(false);
            setAccountOptions(Array.isArray(data) ? data : data?.accounts || []);
        }));
    }, [pickTarget, client?._id, dispatch]);

    useEffect(() => {
        if (!pickTarget || !account?._id) {
            projectsRequestRef.current += 1;
            setProjectOptions([]);
            setProjectsLoading(false);
            return;
        }
        const requestId = ++projectsRequestRef.current;
        setProjectsLoading(true);
        dispatch(getAccountProjects(account._id, "open", (data) => {
            if (projectsRequestRef.current !== requestId) return;
            setProjectsLoading(false);
            setProjectOptions(Array.isArray(data) ? data : data?.projects || []);
        }));
    }, [pickTarget, account?._id, dispatch]);

    useEffect(() => {
        if (!open || !canSeeInternal || !date || !effectiveAccountId) {
            setInvoicedPeriod(null);
            return;
        }
        const requestId = ++invoicedRequestRef.current;
        dispatch(getInvoicedPeriodForDate({ date, accountId: effectiveAccountId }, (data) => {
            if (invoicedRequestRef.current !== requestId) return;
            setInvoicedPeriod(data?.invoiced ? data.period : null);
        }));
    }, [open, canSeeInternal, date, effectiveAccountId, dispatch]);

    const handleSubmit = () => {
        if (submitLockRef.current) return;
        if (isOverageBlocked) return;
        submitLockRef.current = true;
        const timeData = {
            summary,
            time: parseFloat(time) * 60, // Convert hours to minutes
            date,
            ...(canAddOthersTime && selectedUser && { user: selectedUser }),
        };
        onAddTime(timeData, pickTarget ? { client, account, project } : undefined);
        if (keepOpen) {
            setSummary('');
            setTime('');
            setOverageOverride(false);
            return;
        }
        onClose();
    };

    const canSubmit = !isOverageBlocked && !!time && (!pickTarget || !!project);

    return (
        <TopAnchoredDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Add Time
                <CloseButton
                    aria-label="close"
                    onClick={onClose}
                >
                    <Close />
                </CloseButton>
            </DialogTitle>
            <DialogContent>
                <FormContainer>
                    {pickTarget && (
                        <HelperLine variant="body2">
                            Pick the project this work belongs to, then record how long it took.
                        </HelperLine>
                    )}
                    <RetainerOverageNotice status={retainerStatus} requireOverride override={overageOverride} onOverrideChange={setOverageOverride} />
                    {pickTarget && (
                        <>
                            <Autocomplete
                                options={clientOptions}
                                value={client}
                                onChange={(_, v) => { setClient(v); setAccount(null); setProject(null); }}
                                getOptionLabel={(o) => o?.name || ''}
                                isOptionEqualToValue={(o, v) => o._id === v._id}
                                loading={clientsLoading}
                                loadingText="Loading clients…"
                                noOptionsText="No matching client"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Client"
                                        InputProps={{
                                            ...params.InputProps,
                                            // A spinner inside the field rather than helper text below it:
                                            // helper text would shift every field under it as it appears.
                                            endAdornment: (
                                                <>
                                                    {clientsLoading && <CircularProgress color="inherit" size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                options={accountOptions}
                                value={account}
                                onChange={(_, v) => { setAccount(v); setProject(null); }}
                                getOptionLabel={(o) => o?.name || ''}
                                isOptionEqualToValue={(o, v) => o._id === v._id}
                                disabled={!client}
                                loading={accountsLoading}
                                loadingText="Loading accounts…"
                                noOptionsText="No matching account"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Account"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {accountsLoading && <CircularProgress color="inherit" size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                options={projectOptions}
                                value={project}
                                onChange={(_, v) => setProject(v)}
                                getOptionLabel={(o) => o?.name || ''}
                                isOptionEqualToValue={(o, v) => o._id === v._id}
                                disabled={!account}
                                loading={projectsLoading}
                                loadingText="Loading projects…"
                                noOptionsText="No open project on this account"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Project"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {projectsLoading && <CircularProgress color="inherit" size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </>
                    )}
                    <TextField label="What did you work on?" value={summary} onChange={(e) => setSummary(e.target.value)} fullWidth />
                    <TimeDateRow>
                        <TextField
                            label="Time (hours)"
                            type="number"
                            value={time}
                            onChange={(e) => { submitLockRef.current = false; setTime(e.target.value); }}
                            inputProps={{ step: 0.25, min: 0 }}
                            fullWidth
                        />
                        <AppDatePicker
                            label="When did you work on it?"
                            value={date}
                            onChange={(v) => setDate(v)}
                        />
                    </TimeDateRow>
                    {canAddOthersTime && !pickTarget && (
                        <FormControl fullWidth>
                            <InputLabel>User</InputLabel>
                            <Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} label="User">
                                {accountUsers.map((user) => (
                                    user.active && (
                                        <MenuItem key={user._id} value={user._id}>
                                            {user.fullName}
                                        </MenuItem>
                                    )
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {invoicedPeriod && (
                        <Alert severity="info">
                            <AlertTitle>This period has already been invoiced</AlertTitle>
                            {`This client has already been invoiced for ${formatDayLong(invoicedPeriod.start)} – ${formatDayLong(invoicedPeriod.end)}, and the date you picked falls inside it. You can still save this entry — let your manager know, as it may need to roll over onto the next invoice.`}
                        </Alert>
                    )}
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Tooltip title="After you add this record the form stays open, keeping the date and the project you picked. The summary and hours are cleared so you can type the next entry.">
                    <KeepOpenToggle
                        control={<Switch checked={keepOpen} onChange={(event) => setKeepOpen(event.target.checked)} />}
                        label="Keep open to add another"
                    />
                </Tooltip>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!canSubmit}>
                    Add Time
                </Button>
            </DialogActions>
        </TopAnchoredDialog>
    );
};

export default AddTimeRecordDialog;
